import { useEffect, useState, useMemo } from 'react';
import { TopicMap } from '../../../../store/slices/topicSlice';
import { colors } from '../../../../theme/palette';
import { ChartContent } from './ChartContent';
import Highcharts, { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box } from '@material-ui/core';
import { BotRenderedShare } from '../../../../store/models';
import { renderToString } from 'react-dom/server';
import { GraphTooltip } from './GraphTooltip';

export type BotRenderedSharesProps = {
	data: BotRenderedShare[];
	topics: TopicMap;
};

type PageNamesMap = {
	[id: string]: string;
};

// Platform mapping from userAgent
const PLATFORM_MAP = [
	{ key: 'facebook', label: 'Facebook' },
	{ key: 'slack', label: 'Slack' },
	{ key: 'twitter', label: 'Twitter' },
	{ key: 'discord', label: 'Discord' },
	{ key: 'telegram', label: 'Telegram' },
	{ key: 'whatsapp', label: 'WhatsApp' },
	{ key: 'skype', label: 'Skype' },
	{ key: 'linkedin', label: 'LinkedIn' },
	{ key: 'pinterest', label: 'Pinterest' },
	{ key: 'vk', label: 'VK' },
	{ key: 'reddit', label: 'Reddit' },
	{ key: 'quora', label: 'Quora' },
	{ key: 'tumblr', label: 'Tumblr' },
	{ key: 'wechat', label: 'WeChat' },
	{ key: 'line', label: 'Line' },
	{ key: 'viber', label: 'Viber' },
	{ key: 'signal', label: 'Signal' },
	{ key: 'teams', label: 'Teams' },
	{ key: 'yahoo', label: 'Yahoo' },
	{ key: 'duckduckgo', label: 'DuckDuckGo' },
	{ key: 'baidu', label: 'Baidu' },
	{ key: 'yandex', label: 'Yandex' },
	{ key: 'sogou', label: 'Sogou' },
	{ key: 'exabot', label: 'Exabot' },
	{ key: 'ia_archiver', label: 'Alexa' },
];

// Viiision brand colors (cycled with saturation if needed)
const BRAND_COLORS = [
	colors.tealAccent,
	colors.purpleAccent,
	colors.navyAccent,
	colors.blueAccent,
	colors.orangeDark,
	colors.greenDark,
	colors.redDark,
	colors.hotPurpleAccent,
	colors.blueDark,
	colors.orangeHighlighter,
	colors.greenHighlighter,
	colors.purpleAccent20,
	colors.tealAccent20,
	colors.navyAccent20,
	colors.blueAccent20,
	colors.orangeLight,
	colors.greenLight,
	colors.redLight,
	colors.hotPurpleAccent20,
	colors.blueLight,
];

function getPlatform(userAgent: string): string {
	if (!userAgent) return 'Other';
	const ua = userAgent.toLowerCase();
	for (const { key, label } of PLATFORM_MAP) {
		if (ua.includes(key)) return label;
	}
	return 'Other';
}

export const BotRenderedShares = ({ data, topics }: BotRenderedSharesProps) => {
	const [selected, setSelected] = useState<string>(Object.keys(topics)[0]);
	const [pageNames, setPageNames] = useState<PageNamesMap>({});

	const calculatedPageNames = useMemo(() => {
		if (!Object.keys(pageNames).length && Object.keys(topics).length) {
			return Object.keys(topics).reduce(
				(acc, id) => ({
					...acc,
					[id]: topics[id].name.find((lang) => lang.language === 'en')?.name || topics[id].name[0].name,
				}),
				{}
			);
		}
		return pageNames;
	}, [topics, pageNames]);

	useEffect(() => {
		if (Object.keys(calculatedPageNames).length > 0) {
			setPageNames(calculatedPageNames);
		}
	}, [calculatedPageNames]);

	// Prepare chart data: group by date, stack by platform
	const chartData = useMemo(() => {
		// Filter for selected exhibitId
		const filtered = data.filter((event) => event.metadata.exhibitId === selected);
		// Map: date -> platform -> count
		const dateMap = new Map();
		for (const event of filtered) {
			const dateObj = new Date(event.metadata.dateRendered);
			const date = dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
			const platform = getPlatform(event.metadata.userAgent);
			if (!dateMap.has(date)) dateMap.set(date, {});
			const platformMap = dateMap.get(date);
			platformMap[platform] = (platformMap[platform] || 0) + 1;
		}
		const categories = Array.from(dateMap.keys());
		const platforms = Array.from(
			new Set([].concat(...Array.from(dateMap.values()).map((obj) => Object.keys(obj))))
		);
		const series = platforms.map((platform, idx) => ({
			name: platform,
			data: categories.map((date) => dateMap.get(date)[platform] || 0),
			color: BRAND_COLORS[idx % BRAND_COLORS.length],
		}));
		return { categories, series };
	}, [data, selected]);

	const chartOptions: Options = {
		chart: {
			type: 'column',
			height: 250,
			zoomType: 'xy',
			panning: { enabled: true, type: 'xy' },
			panKey: 'shift',
		},
		title: { text: '' },
		xAxis: {
			categories: chartData.categories,
			labels: {
				enabled: true,
				useHTML: true,
				style: { color: colors.grayDark, fontSize: '12px', lineHeight: '20px' },
			},
		},
		yAxis: {
			title: { text: null },
			labels: { style: { fontSize: '12px', lineHeight: '20px' } },
			stackLabels: { enabled: true },
		},
		legend: { enabled: true, align: 'center', verticalAlign: 'bottom', layout: 'horizontal' },
		plotOptions: {
			series: { stacking: 'normal', cursor: 'pointer' },
		},
		series: chartData.series as any,
		credits: { enabled: false },
		tooltip: {
			useHTML: true,
			backgroundColor: colors.grayDark,
			borderWidth: 0,
			borderRadius: 4,
			padding: 0,
			formatter: function () {
				// this.x is the date, this.series.name is the platform, this.y is the value
				const date = this.x;
				const platform = this.series.name;
				const value = this.y;
				// Find the series data for this platform
				const seriesData = this.series.data.map((point) => point.y);
				const index = this.point.index;
				const prevValue = index > 0 ? seriesData[index - 1] : null;
				const change =
					prevValue && prevValue !== 0 ? Math.round(((value - prevValue) / prevValue) * 100) : null;
				return renderToString(
					<GraphTooltip title={platform} subtitle={`Displayed on: ${date}`} value={value} change={change} />
				);
			},
		},
	};

	return (
		<ChartContent
			name={'IMPRESSIONS'}
			selected={selected}
			pageNames={pageNames}
			setSelected={setSelected}
			total={chartData.series.reduce((sum, s) => sum + s.data.reduce((a, b) => a + b, 0), 0)}
			avgSession={''}
			toolTipMsg={'How many times a shared link was rendered on social media, in SMS, or on the web'}
		>
			<Box sx={{ width: '100%' }}>
				<HighchartsReact highcharts={Highcharts} options={chartOptions} />
			</Box>
		</ChartContent>
	);
};
